/* ==========================================================================
   pk-bottom-stack-fix.js - 2026-08-04 (rev 3)

   PROBLEM
   Two pages render a fixed bottom action bar on mobile:

     .pkcv__mobile-bar    - PDP Add to Cart, z-index 9990. Defined in
                            assets/peekora-conversion-product.css and used by
                            BOTH sections/peekora-product-page.liquid and
                            sections/peekora-mr-product.liquid.
     .pq-cart__mobile-bar - Cart page Checkout, z-index 40. Defined in
                            assets/peekora-cart-page.css, shown below 620px.

   Three things were landing on those buttons: the floating product video
   (.pksv__player, z-index 9998), the chat bubble, and an AI / discount toast.

   REVISION HISTORY - each rev fixed a real bug, so do not "simplify" back.

   rev 1  Walked two levels down from <body>. The video player sits three levels
          down (body > .shopify-section > [data-pksv] > .pksv__player) and was
          never inspected. Guessing at nesting depth is the wrong model.

   rev 2  Replaced depth-guessing with document.elementsFromPoint() probes, which
          return the real paint stack. Fixed the video and the toast. Chat
          survived, because of the two bugs rev 3 addresses:

   rev 3  (a) Chat widgets commonly render a FULL-VIEWPORT fixed shell with
              pointer-events:none, and position the bubble absolutely inside it.
              rev 2 climbed to the nearest position:fixed ancestor, found that
              full-screen shell, saw height > the 45% panel threshold, and
              skipped it - so nothing moved. rev 3 instead picks the OUTERMOST
              still-small positioned box, which is the bubble wrapper.
          (b) Element.parentElement returns null at a shadow root boundary, so
              a bubble inside an open shadow root ended the climb immediately.
              parentOf() now hops to the shadow host.

   DETECTION
   Primary detection is elementsFromPoint() probed at five points across the bar:
   anything returned before the bar is genuinely painting over it, whatever its
   depth. A bounded depth-4 tree walk supplements it, because elementsFromPoint
   skips pointer-events:none elements - those do not block taps but still hide
   the button.

   WHY THE REGISTRY IS STICKY
   Once lifted, an element is NOT released for no longer overlapping - it stopped
   overlapping BECAUSE it was lifted, and releasing on that basis oscillates
   every frame. Release happens only on disconnect, or on growth past the panel
   threshold (chat opened).

   THE FLOATING VIDEO IS HANDLED DIFFERENTLY - ON PURPOSE
   It is excluded from the generic lifter and driven through the --guard custom
   property sections/pk-sticky-product-video.liquid already exposes. Its own
   measureGuard() computes 0 because it only scans document.body.children and
   the bar is nested deeper. Its expanded state re-anchors to bottom:50% with a
   centring transform, which a blanket margin-bottom would knock off-centre.

   WHY margin-bottom AND NOT transform OR bottom
   For a fixed OR absolutely positioned box anchored with `bottom`, margin-bottom
   offsets it from that edge, and it does not clobber the transform chat widgets
   use for open/close animation.

   WHY NO z-index IS TOUCHED
   Raising either bar would put it above the mini cart drawer and story modal,
   which are meant to cover the page.

   DIAGNOSTICS
   Load any page with ?pkdebug=1 to print the paint stack over the bar and what
   was chosen, to the console. Nothing is logged otherwise.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__PK_BOTTOM_STACK_FIX__) return;
  window.__PK_BOTTOM_STACK_FIX__ = true;

  var BAR_SELECTOR = '.pkcv__mobile-bar,.pq-cart__mobile-bar,[data-pk-sticky-atc]';
  /* pksv is excluded here and steered via --guard instead - see header. */
  var OURS = '[class*="pkcv"],[class*="pkmr"],[class*="peekora"],[class*="pq-cart"],[class*="pk-mini-cart"],[class*="pkmc"],[class*="pksv"],[id^="pksv-"],[id*="pk-mini-cart"],[id*="PkOfficial"]';
  var PROBES = [0.08, 0.25, 0.5, 0.75, 0.92];
  var GAP = 12;
  var LADDER = [250, 600, 1200, 2500, 4000, 7000, 11000];
  var SKIP_TAGS = { SCRIPT: 1, STYLE: 1, LINK: 1, NOSCRIPT: 1, TEMPLATE: 1, SVG: 1, PATH: 1, IMG: 1, VIDEO: 1, SOURCE: 1, PICTURE: 1, BR: 1, HR: 1 };

  var DEBUG = false;
  try { DEBUG = window.location.search.indexOf('pkdebug=1') !== -1; } catch (e) {}
  function log() {
    if (!DEBUG || !window.console) return;
    try { console.log.apply(console, ['[pk-bottom-stack]'].concat([].slice.call(arguments))); } catch (e) {}
  }
  function describe(el) {
    if (!el) return 'null';
    var cs = window.getComputedStyle(el);
    var r = el.getBoundingClientRect();
    return el.tagName.toLowerCase()
      + (el.id ? '#' + el.id : '')
      + (typeof el.className === 'string' && el.className ? '.' + el.className.trim().split(/\s+/).join('.') : '')
      + ' {pos:' + cs.position + ' pe:' + cs.pointerEvents
      + ' ' + Math.round(r.width) + 'x' + Math.round(r.height) + '}';
  }

  var lifted = new Map();
  var queued = false;

  function isShown(el) {
    var cs = window.getComputedStyle(el);
    return cs.display !== 'none' && cs.visibility !== 'hidden';
  }

  function isOurs(el) {
    return !!(el.closest && el.closest(OURS));
  }

  /* Hops the shadow root boundary, where parentElement is null. */
  function parentOf(node) {
    if (node.parentElement) return node.parentElement;
    var rootNode = node.getRootNode ? node.getRootNode() : null;
    if (rootNode && rootNode.host) return rootNode.host;
    return null;
  }

  function findBar() {
    var els = document.querySelectorAll(BAR_SELECTOR);
    var best = null;
    var bestH = 0;
    for (var i = 0; i < els.length; i++) {
      if (!isShown(els[i])) continue;
      var h = els[i].getBoundingClientRect().height;
      if (h > bestH) { bestH = h; best = els[i]; }
    }
    return bestH > 0 ? best : null;
  }

  /* Climbs from a probe hit and returns the OUTERMOST positioned box that is
     still small enough to be a bubble or a toast. Climbing past the last small
     box is what finds the real widget wrapper rather than the icon inside it;
     stopping at the first large fixed ancestor is what avoids grabbing a
     full-viewport shell. */
  function chooseTarget(hit, maxH, vw, bar) {
    var best = null;
    var node = hit;
    var passthroughShell = null;

    while (node && node.nodeType === 1 && node !== document.body && node !== document.documentElement) {
      if (node === bar || bar.contains(node) || isOurs(node)) return null;

      var cs = window.getComputedStyle(node);
      var pos = cs.position;
      var r = node.getBoundingClientRect();
      var positioned = pos === 'fixed' || pos === 'absolute' || pos === 'sticky';

      if (positioned && r.height > 0 && r.width > 0 && r.height <= maxH && r.width <= vw * 0.99) {
        best = node;
      }

      /* A big fixed box that ignores pointer events is a pass-through shell.
         Moving it moves everything inside it and blocks nothing, so keep it as
         a fallback - but only use it if no small box was found. */
      if (pos === 'fixed' && r.height > maxH) {
        if (cs.pointerEvents === 'none') passthroughShell = node;
        break;
      }

      node = parentOf(node);
    }

    return best || passthroughShell;
  }

  function probeBlockers(bar, rect, maxH) {
    var found = [];
    if (!document.elementsFromPoint) return found;
    var vw = window.innerWidth || document.documentElement.clientWidth;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var y = Math.min(vh - 2, Math.max(1, Math.round(rect.top + rect.height / 2)));

    for (var p = 0; p < PROBES.length; p++) {
      var x = Math.min(vw - 2, Math.max(1, Math.round(vw * PROBES[p])));
      var stack = document.elementsFromPoint(x, y) || [];
      for (var i = 0; i < stack.length; i++) {
        var el = stack[i];
        if (el === bar || bar.contains(el)) break;
        log('over bar at x=' + x + ':', describe(el));
        var target = chooseTarget(el, maxH, vw, bar);
        if (target && found.indexOf(target) === -1) {
          log('  -> lifting', describe(target));
          found.push(target);
        }
      }
    }
    return found;
  }

  /* Supplement: catches pointer-events:none overlays elementsFromPoint skips. */
  function walkCandidates() {
    var out = [];
    var budget = 3000;
    (function walk(node, depth) {
      if (depth > 4 || budget <= 0) return;
      var kids = node.children;
      for (var i = 0; i < kids.length && budget > 0; i++) {
        var k = kids[i];
        budget--;
        if (SKIP_TAGS[String(k.tagName).toUpperCase()]) continue;
        out.push(k);
        walk(k, depth + 1);
      }
    })(document.body, 1);
    return out;
  }

  function lift(el, px) {
    if (!lifted.has(el)) lifted.set(el, el.style.marginBottom || '');
    var want = px + 'px';
    if (el.style.marginBottom !== want) el.style.setProperty('margin-bottom', want, 'important');
  }

  function restore(el) {
    var original = lifted.get(el);
    lifted['delete'](el);
    if (original) el.style.setProperty('margin-bottom', original);
    else el.style.removeProperty('margin-bottom');
  }

  function releaseAll() {
    var keys = [];
    lifted.forEach(function (value, el) { keys.push(el); });
    keys.forEach(restore);
  }

  function syncStickyVideo(px) {
    var roots = document.querySelectorAll('[data-pksv]');
    var want = px + 'px';
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].style.getPropertyValue('--guard') !== want) {
        roots[i].style.setProperty('--guard', want);
      }
    }
  }

  function apply() {
    queued = false;

    var root = document.documentElement;
    var bar = findBar();

    if (!bar) {
      root.style.setProperty('--pk-atc-h', '0px');
      document.body.classList.remove('pk-atc-bar-on');
      syncStickyVideo(0);
      releaseAll();
      log('no bar on screen');
      return;
    }

    var rect = bar.getBoundingClientRect();
    var barH = Math.round(rect.height);
    root.style.setProperty('--pk-atc-h', barH + 'px');
    document.body.classList.add('pk-atc-bar-on');

    /* The video adds its own 16px --gap on top of this. */
    syncStickyVideo(barH);

    var offset = barH + GAP;
    var vh = window.innerHeight || root.clientHeight;
    var maxH = Math.min(260, vh * 0.45);
    log('bar', describe(bar), 'offset', offset);

    var targets = probeBlockers(bar, rect, maxH);

    var pool = walkCandidates();
    for (var i = 0; i < pool.length; i++) {
      var el = pool[i];
      if (el === bar || bar.contains(el) || el.contains(bar) || isOurs(el)) continue;
      if (targets.indexOf(el) !== -1) continue;
      var cs = window.getComputedStyle(el);
      if (cs.position !== 'fixed' || cs.display === 'none' || cs.visibility === 'hidden') continue;
      var r = el.getBoundingClientRect();
      var floating = r.width > 0 && r.height > 0 && r.height <= maxH;
      var bottomAnchored = r.bottom >= vh - offset - 40 && r.top < vh;
      if (floating && bottomAnchored) { log('walk found', describe(el)); targets.push(el); }
    }

    for (var n = 0; n < targets.length; n++) lift(targets[n], offset);

    /* Release only on gone / grown. */
    var stale = [];
    lifted.forEach(function (value, el2) {
      if (!el2.isConnected || el2.getBoundingClientRect().height > maxH) stale.push(el2);
    });
    stale.forEach(restore);
  }

  function schedule() {
    if (queued) return;
    queued = true;
    if (window.requestAnimationFrame) window.requestAnimationFrame(apply);
    else window.setTimeout(apply, 16);
  }

  function boot() {
    schedule();
    LADDER.forEach(function (ms) { window.setTimeout(schedule, ms); });

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('orientationchange', schedule, { passive: true });
    window.addEventListener('load', schedule, { passive: true });
    document.addEventListener('shopify:section:load', schedule);
    document.addEventListener('peekora:cart:updated', schedule);

    if (window.ResizeObserver) {
      var ro = new ResizeObserver(schedule);
      var watched = null;
      var watch = function () {
        var b = findBar();
        if (b && b !== watched) { watched = b; ro.observe(b); }
      };
      watch();
      LADDER.forEach(function (ms) { window.setTimeout(watch, ms); });
      document.addEventListener('shopify:section:load', watch);
    }

    if (window.MutationObserver) {
      new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          if (records[i].addedNodes.length || records[i].removedNodes.length) { schedule(); return; }
        }
      }).observe(document.body, { childList: true });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
